/**
 * The Inspector's dock beside the canvas (2026-10-05).
 *
 * Operator: "on desktop the inspector doesn't have to overlap the canvas,
 * when the inspector is open the canvas could shift left?" Measured before
 * this: the panel covered the right 56 px of a 1080-wide canvas in a 1600
 * window and 186 px of an 1180-wide one at 1440, with the bands either side
 * of the canvas sitting empty.
 *
 * So while the Inspector is open, and only where there is room, the canvas
 * gives up a column on the right for it: App fits the shell into the rest
 * of the window and slides it left, and the panel stands in that column at
 * full height. Where the room is not there (the iPad, whose canvas fills
 * the screen, or a narrow desktop window) nothing changes: the panel
 * overlaps as before rather than shrinking the stage you play.
 *
 * Selection lands on release, never on press (EditableItem's click-vs-drag
 * arbiter), so the slide can never move an item out from under a drag.
 */

import { createSignal } from 'solid-js';
import { fitScale } from './devices.ts';

/** The docked panel's width, and the gap kept either side of it. */
export const DOCK_PANEL_W = 320;
export const DOCK_GAP = 16;
/** What the canvas gives up while the Inspector is docked. */
export const DOCK_RESERVE_W = DOCK_PANEL_W + 2 * DOCK_GAP;
/** Dock only while the canvas keeps at least this share of the size it has
 *  without the dock. A window with spare room either side just slides; a
 *  window that would have to shrink the stage by more than this keeps the
 *  overlap, because a canvas that visibly shrinks every time a control is
 *  clicked is worse than a panel over its edge. */
export const DOCK_MIN_KEEP = 0.85;

const [docked, setDocked] = createSignal(false);
/** True while the Inspector stands in its own column beside the canvas. */
export const inspectorDocked = docked;
export const setInspectorDocked = setDocked;

/**
 * Pure: does the Inspector dock, and at what scale is the shell then drawn.
 * `wanted` is "the Inspector is showing, on a screen with a real pointer".
 */
export function dockDecision(
  size: { w: number; h: number },
  avail: { w: number; h: number },
  wanted: boolean,
  readableFloor: number,
  cap = 1,
): { dock: boolean; scale: number } {
  const undocked = fitScale(size, avail, cap);
  if (!wanted) return { dock: false, scale: undocked };
  const w = avail.w - DOCK_RESERVE_W;
  if (w <= 0) return { dock: false, scale: undocked };
  const scale = fitScale(size, { w, h: avail.h }, cap);
  if (scale < readableFloor || scale < undocked * DOCK_MIN_KEEP) return { dock: false, scale: undocked };
  return { dock: true, scale };
}
